import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addCustomerDocument,
  addCustomerSites,
  addNewCustomerNote,
  changeCustomerStatus,
  createCustomer,
  customerSearch,
  deleteBulkCustomerNotes,
  deleteCustomerDocument,
  deleteCustomers,
  editCustomerDocument,
  getAllCustomerCityByMspId,
  getAllCustomerExternalContacts,
  getAllCustomers,
  getCustomerBillingInvoice,
  getCustomerById,
  getCustomerDetails,
  getCustomerDocuments,
  getCustomerNotes,
  removeCustomerLogo,
  updateCustomerBillingInvoice,
  updateCustomerContacts,
  updateCustomerDefaultSla,
  updateCustomerDetails,
  updateCustomerLogo,
  updateCustomerNote,
} from "controller/api";
import {
  CreateCustomerInput,
  DeleteCustomerDocumentsInput,
  DeleteCustomersInput,
  GetCustomerDocumentsInput,
  GetQueryProps,
  CusotomerBillingInvoice,
  AddNewCustomerNoteInput,
  UpdateCustomerDefaultSla,
  ApiErrorType,
  UpdateCustomerNote,
} from "shared";
import { sentryLogger } from "utilz";

export const useCustomerSearch = (searchQuery: string) => {
  return useQuery({
    queryKey: ["customerSiteDropdown", searchQuery],
    queryFn: () => customerSearch(searchQuery),
    onError: (error) => console.log(error),
    // enabled: searchQuery ? true : false,
  });
};

export const useGetCustomerById = (id: string) => {
  return useQuery({
    queryKey: ["customer", id],
    queryFn: () => getCustomerById(id),
    onError: (error) => console.log(error),
    enabled: id ? true : false,
  });
};
export const useGetAllCustomers = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  enabled = true,
}: GetQueryProps & { enabled?: boolean }) => {
  return useQuery({
    queryKey: [
      "customers",
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    ],
    queryFn: () =>
      getAllCustomers({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    enabled: enabled,
    // Customers are slow-changing and this response is large (the api defaults
    // to perPageCount 1000), so don't refetch it on every remount. Every
    // customer write — create, delete, detail update, status change — now
    // invalidates ["customers"], so a stale window can't outlive an edit.
    staleTime: 5 * 60 * 1000,
    onError: (error: ApiErrorType) => {
      console.log(error);
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getAllCustomers",
      });
    },
  });
};

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries(["customers"]);
      queryClient.invalidateQueries(["customerSiteDropdown"]);
    },
    onError: (error: ApiErrorType, variable) => {
      sentryLogger({
        error: error,
        data: variable,
        workflow: "customer",
        level: "error",
        functionName: "createCustomer",
      });
    },
  });
};

export const useGetAllCustomerCityByMspId = () => {
  return useQuery({
    queryKey: ["customerCities"],
    queryFn: () => getAllCustomerCityByMspId(),
    onError: (error: ApiErrorType) => {
      console.log(error);
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getAllCustomerCityByMspId",
      });
    },
  });
};

export const useGetCustomerDocuments = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  customerId,
}: GetCustomerDocumentsInput) => {
  return useQuery({
    queryKey: ["customer_documents", customerId],
    queryFn: () =>
      getCustomerDocuments({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        customerId,
      }),
    onError: (error: ApiErrorType) => {
      console.log(error);
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getCustomerDocuments",
      });
    },
  });
};

export const useDeleteCustomerDocument = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (value: DeleteCustomerDocumentsInput) => deleteCustomerDocument(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries([
          "customer_documents",
          variables.customerId,
        ]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "deleteCustomerDocument",
        });
      },
    }
  );
};

export const useDeleteCustomers = () => {
  const queryClient = useQueryClient();

  return useMutation((value: DeleteCustomersInput) => deleteCustomers(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["customers"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "customer",
        level: "error",
        functionName: "deleteCustomers",
      });
    },
  });
};

export const useUpdateCustomerBillingInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      data,
      customerIdParams,
    }: {
      data: CusotomerBillingInvoice;
      customerIdParams: string;
    }) => updateCustomerBillingInvoice({ data, customerIdParams }),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["customers"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "updateCustomerBillingInvoice",
        });
      },
    }
  );
};

export const useGetCustomerBillingInvoice = ({
  customerIdParams,
  is_customer_overview = true, //defualtly its true now neew to chang to false for customer billing address in response
}: {
  customerIdParams: string | number;
  is_customer_overview?: boolean;   
}) => {
  return useQuery({
    queryKey: ["customers", "billing-invoice", customerIdParams, is_customer_overview],
    queryFn: () =>
      getCustomerBillingInvoice({ customerIdParams, is_customer_overview }),
    onError: (error: ApiErrorType) => {
      console.log(error);
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getCustomerBillingInvoice",
      });
    },
  });
};

type GetQueryPropsWithCustomerId = GetQueryProps & { customerId: string };
export const useGetCustomerNotes = ({
  customerId,
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryPropsWithCustomerId) => {
  return useQuery({
    queryKey: ["customer_notes", customerId],
    queryFn: () =>
      getCustomerNotes({
        customerId,
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    onError: (error: ApiErrorType) => {
      console.log(error),
        sentryLogger({
          error: error,
          workflow: "customer",
          level: "error",
        });
    },
  });
};

export const useAddCustomerNote = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerId, noteData }: AddNewCustomerNoteInput) =>
      addNewCustomerNote({ customerId, noteData }), // Call addNewCustomerNote with the provided parameters
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_notes"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "addNewCustomerNote",
        });
      },
    }
  );
};

export const useUpdateCustomerNote = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ noteId, customerId, noteData }: UpdateCustomerNote) =>
      updateCustomerNote({ noteId, customerId, noteData }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_notes"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "updateCustomerNote",
        });
      },
    }
  );
};

export const useAddCustomerDocument = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerId, formData }: any) =>
      addCustomerDocument({ customerId, formData }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_documents"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "addCustomerDocument",
        });
      },
    }
  );
};

export const useEditCustomerDocument = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ attachment_id, data }: { attachment_id: string; data: any }) =>
      editCustomerDocument({ attachment_id, data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_documents"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "editCustomerDocument",
        });
      },
    }
  );
};

export const useDeleteBulkCustomerNotes = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (noteIds: string[]) => deleteBulkCustomerNotes({ noteIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_notes"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "deleteBulkCustomerNotes",
        });
      },
    }
  );
};

export const useGetCustomerDetails = ({ customerId }: { customerId: any }) => {
  return useQuery({
    queryKey: ["customer_detail", customerId],
    queryFn: () => getCustomerDetails({ customerId }),
    enabled: customerId ? true : false,
    onSuccess: (data) => {},
    onError: (error: ApiErrorType) => {
      console.log(error);
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getCustomerDetails",
      });
    },
  });
};

export const useUpdateCustomerDetails = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerId, bodyData }: { customerId: string; bodyData: any }) =>
      updateCustomerDetails({ customerId, bodyData }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_detail"]);
        // The list caches carry customer_name — without this a rename leaves
        // every cached ["customers"] slot (filter dropdowns, pickers) showing
        // the old name until those queries happen to remount.
        queryClient.invalidateQueries(["customers"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "updateCustomerDetails",
        });
      },
    }
  );
};

export const useUpdateCustomerLogo = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerId, file }: { customerId: string; file: any }) =>
      updateCustomerLogo({ customerId, file }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_detail"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "updateCustomerLogo",
        });
      },
    }
  );
};

export const useRemoveCustomerLogo = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerId }: { customerId: string }) =>
      removeCustomerLogo({ customerId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_detail"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "removeCustomerLogo",
        });
      },
    }
  );
};

export const useUpdateCustomerDefaultSla = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variable: UpdateCustomerDefaultSla) =>
      updateCustomerDefaultSla(variable),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["customer", variables.customerId]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "customer",
        level: "error",
        functionName: "updateCustomerDefaultSla",
      });
    },
  });
};

export const useUpdateCustomerContacts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomerContacts,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["customer", variables.customerId]);
      queryClient.invalidateQueries(["customer_detail", variables.customerId]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "customer",
        level: "error",
        functionName: "updateCustomerContacts",
      });
    },
  });
};

export const useChangeCustomerStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: changeCustomerStatus,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["customer", variables.customerId]);
      queryClient.invalidateQueries(["customer_detail", variables.customerId]);
      // Lists filter on is_active, so a status change moves a row in or out of
      // them — the list key has to be invalidated alongside the detail keys.
      queryClient.invalidateQueries(["customers"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "customer",
        level: "error",
        functionName: "changeCustomerStatus",
      });
    },
  });
};

export const useGetAllCustomerExternalContacts = ({
  customerId,
  searchQuery,
}: {
  customerId: string;
  searchQuery: string;
}) => {
  const params = { customerId, searchQuery };

  return useQuery({
    queryKey: ["customer_external_contacts", customerId, searchQuery], // Include customerId in queryKey
    queryFn: () => getAllCustomerExternalContacts(params),
    enabled: !!customerId, // Only run query when customerId exists
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getAllCustomerExternalContacts",
      });
    },
  });
};
